import { find } from 'lodash';
import React, { FC, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Body, Row, Section } from 'src/presentation/components/forms/Layout';
import { Button } from 'src/presentation/components/agent_desktop_components';
import Dropdown from 'src/presentation/components/forms/Dropdown';
import FieldToggle from 'src/presentation/components/forms/FieldToggle';
import Form, { FormEditorProps, useForm } from 'src/presentation/components/forms/Form';
import Footer from 'src/presentation/components/forms/Footer';
import generateId from 'src/lib/generateId';
import { H1 } from '@ad-scripts/presentation/components/common/headers';
import InputField from 'src/presentation/components/forms/InputField';
import skillsResource from 'src/actions/skills/skillsResource';
import { useDropdownOptions } from './hierarchy';
import { useResourceValues } from 'src/presentation/hooks/useResource';

const dataAidPrefix = 'admin-skills';
const parentIdAttr = 'parentId';

const SkillEditor: FC<FormEditorProps> = ({ errors, item, root }) => {
  const { values: skills } = useResourceValues(skillsResource);
  const { add, update, validate } = skillsResource.effects;
  const { resetErrors } = skillsResource.actions;
  const [parentDropdownEnabled, setParentDropdownEnabled] = useState(item ? !!item.parentId : false);

  const formContext = useForm({
    add,
    defaultItem: { disabled: false, name: '', _version: 1 },
    errors,
    item,
    resetErrors,
    root,
    update,
    validate,
    onSave: useOnSave(parentDropdownEnabled),
  });

  const { workingState, onClose, onSave, isNew } = formContext;
  const dropdownOptions = useDropdownOptions(skills, item)
    .filter(item => !item.disabled)
    .map(({ label, value }) => ({ label, value }));
  const selectedDropdownOption = find(dropdownOptions, o => o.value === workingState[parentIdAttr]);

  return (
    <Form context={formContext}>
      <H1>{isNew ? 'Add' : 'Update'} Skill</H1>

      <Body data-aid={`${dataAidPrefix}-editor`}>
        <Section>
          <Row>
            <InputField
              data-aid={`${dataAidPrefix}-editor-name-input`}
              attr="name"
              autoFocus
              label="Skill Name"
              placeholder="Enter name"
            />
          </Row>
          <Row>
            <FieldToggle
              dataAid={`${dataAidPrefix}-toggle-parent-dropdown`}
              attr={parentIdAttr}
              doNotExpandContent
              label="Nest skill under"
              onChange={(isEnabled, currentVal) => {
                if (isEnabled) {
                  setParentDropdownEnabled(true);
                  return currentVal;
                }
                setParentDropdownEnabled(false);
                return '';
              }}
            >
              {isEnabled => (
                <Dropdown
                  data-aid={`${dataAidPrefix}-parent-dropdown`}
                  attr={parentIdAttr}
                  disabled={!isEnabled}
                  options={dropdownOptions}
                  placeholder="Select parent skill"
                  searchable
                  text={selectedDropdownOption ? selectedDropdownOption.label : undefined}
                  value={workingState[parentIdAttr]}
                />
              )}
            </FieldToggle>
          </Row>
        </Section>
      </Body>

      <Footer>
        <Button data-aid={`${dataAidPrefix}-editor-cancel`} type={Button.Types.TEXT} onClick={onClose}>
          Cancel
        </Button>
        <Button data-aid={`${dataAidPrefix}-editor-save`} onClick={onSave}>
          {isNew ? 'Add Skill' : 'Save'}
        </Button>
      </Footer>
    </Form>
  );
};

function useOnSave(parentDropdownEnabled) {
  const dispatch = useDispatch();
  return ({ add, isNew, item, resetErrors, update, validate, workingStateRef }) => {
    dispatch(resetErrors());

    const currentWorkingState = workingStateRef.current;
    currentWorkingState.name = currentWorkingState.name.trim();
    const dataToValidate = { ...currentWorkingState, parentDropdownEnabled };
    if (validate && !dispatch(validate(dataToValidate))) {
      return;
    }

    const customToastMsg = `${isNew ? 'Added' : 'Updated'} ${currentWorkingState.name}`;

    isNew
      ? dispatch(add({}, { id: generateId(), ...currentWorkingState }, { customToastMsg }))
      : dispatch(
          update(
            { id: item.id },
            { ...currentWorkingState, _version: currentWorkingState._version + 1 },
            { customToastMsg }
          )
        );
  };
}

export default SkillEditor;
